import {
  Address,
  City,
  County,
  CountyFips,
  DateOfBirth,
  Email,
  FederalPovertyLevel,
  FirstName,
  Gender,
  HouseholdIncome,
  HouseholdSize,
  LastName,
  QualifyingLifeEvent,
  TobaccoUse,
  USAState,
  USAStateAcronym,
  UsPhone,
  UUID,
  ZipCode,
} from '@hc-frontend/core-types';
import { z } from 'zod';

export const MemberType = z.enum(['PRIMARY', 'SPOUSE', 'CHILD']);
export type MemberType = z.infer<typeof MemberType>;

export const MemberBase = z.object({
  firstName: FirstName.optional(),
  lastName: LastName.optional(),
  gender: Gender.optional(),
  tobaccoUse: TobaccoUse.optional(),
  dateOfBirth: DateOfBirth.optional(),
});

// TODO validate 18 - 99
export const Spouse = MemberBase.extend({
  memberType: z.literal(MemberType.Enum.SPOUSE),
});

export type Spouse = z.infer<typeof Spouse>;

// TODO validate 18 - 99
export const Applicant = MemberBase.extend({
  memberType: z.literal(MemberType.Enum.PRIMARY),
});

export type Applicant = z.infer<typeof Applicant>;

// TODO validate 0 - 29
export const Child = MemberBase.extend({
  memberType: z.literal(MemberType.Enum.CHILD),
});

export type Child = z.infer<typeof Child>;

export const ContactInformation = z
  .object({
    firstName: FirstName,
    lastName: LastName,
    email: Email,
    address: Address,
    phoneNumber: UsPhone,
  })
  .partial();
export type ContactInformation = z.infer<typeof ContactInformation>;

export const Location = z.object({
  city: City,
  stateAcronym: USAStateAcronym,
  state: USAState,
  zipCode: ZipCode,
  county: County,
  countyFips: CountyFips,
});

export type Location = z.infer<typeof Location>;

const ProspectBase = z.object({
  location: Location.optional(),
  householdSize: HouseholdSize.optional(),
  householdIncome: HouseholdIncome.optional(),
  federalPovertyLevel: FederalPovertyLevel.optional(), // FPL
  qualifyingLifeEvent: QualifyingLifeEvent.optional(), // QLE
  applicant: Applicant.optional(),
  spouse: Spouse.optional(),
  children: Child.array().max(4).default([]),
  contactInformation: ContactInformation,
  leadId: UUID.optional(),
  sold: z.boolean(),
});

type ProspectBase = z.infer<typeof ProspectBase>;

function transform(data: ProspectBase) {
  const contactInformation = { ...data.contactInformation };
  const firstName = data.applicant?.firstName;
  const lastName = data.applicant?.lastName;

  if (firstName) {
    contactInformation.firstName = firstName;
  }

  if (lastName) {
    contactInformation.lastName = lastName;
  }

  return {
    ...data,
    contactInformation,
    householdSize:
      data.householdSize ??
      data.children.length + (data.applicant ? 1 : 0) + (data.spouse ? 1 : 0),
  };
}

export const Prospect = ProspectBase.transform(transform);

export type Prospect = z.infer<typeof Prospect>;

export const Lead = ProspectBase.required()
  .partial({
    spouse: true,
  })
  .transform(transform);

export type Lead = z.infer<typeof Lead>;

export const LeadStatus = z.enum([
  'FRESH2',
  'PURCHASE_COMPLETED',
  'PROSPECT',
  'SUBMITTED',
  'ENRICHED',
  'SCORED',
  'DELIVERED',
  'ROUTED',
]);

export type LeadStatus = z.infer<typeof LeadStatus>;
